import { Injectable, inject } from '@angular/core';
import { AnalyticsEventName, AnalyticsService } from '@lite-workspace/shared/services/analytics';

/** Thin wrappers for the cabinet UI surface events (designs / account / editor / shell). */
@Injectable({ providedIn: 'root' })
export class UiAnalyticsService {
  private readonly analytics = inject(AnalyticsService);

  designsListViewed(input: { designsCount: number }): void {
    this.analytics.track(AnalyticsEventName.DESIGNS_LIST_VIEWED, { designs_count: input.designsCount });
  }

  designAddClicked(source: 'designs_page' | 'shell_cta'): void {
    this.analytics.track(AnalyticsEventName.DESIGN_ADD_CLICKED, { source });
  }

  designOpened(input: { designId: string }): void {
    this.analytics.track(AnalyticsEventName.DESIGN_OPENED, { design_id: input.designId });
  }

  designEditorOpened(input: { designId: string }): void {
    this.analytics.track(AnalyticsEventName.DESIGN_EDITOR_OPENED, { design_id: input.designId });
  }

  profileMenuOpened(): void {
    this.analytics.track(AnalyticsEventName.PROFILE_MENU_OPENED, {});
  }

  pricingViewed(): void {
    this.analytics.track(AnalyticsEventName.PRICING_VIEWED, {});
  }

  balanceViewed(): void {
    this.analytics.track(AnalyticsEventName.BALANCE_VIEWED, {});
  }

  bonusPromoClicked(): void {
    this.analytics.track(AnalyticsEventName.BONUS_PROMO_CLICKED, {});
  }

  themeSwitched(theme: 'light' | 'dark'): void {
    this.analytics.track(AnalyticsEventName.THEME_SWITCHED, { theme });
  }

  logout(source: 'account_menu' | 'shell'): void {
    this.analytics.track(AnalyticsEventName.AUTH_LOGOUT, { source });
  }

  settingsOpened(input?: { surface: 'account' | 'project'; projectId?: string }): void {
    this.analytics.track(AnalyticsEventName.SETTINGS_OPENED, {
      surface: input?.surface ?? 'account',
      ...(input?.projectId ? { project_id: input.projectId } : {}),
    });
  }

  settingsChanged(input: { field: string; surface?: 'account' | 'project'; projectId?: string }): void {
    this.analytics.track(AnalyticsEventName.SETTINGS_CHANGED, {
      field: input.field,
      surface: input.surface ?? 'account',
      ...(input.projectId ? { project_id: input.projectId } : {}),
    });
  }

  supportOpened(): void {
    this.analytics.track(AnalyticsEventName.SUPPORT_OPENED, {});
  }

  supportChannelPicked(channel: string): void {
    this.analytics.track(AnalyticsEventName.SUPPORT_CHANNEL_PICKED, { channel });
  }

  /** Never sends the query text — length + outcome only (PII). */
  searchUsed(input: { surface: 'designs' | 'projects' | 'photos'; queryLength: number; resultsCount: number }): void {
    this.analytics.track(AnalyticsEventName.SEARCH_USED, {
      surface: input.surface,
      query_length: input.queryLength,
      results_count: input.resultsCount,
    });
  }

  viewModeToggled(input: { surface: 'designs' | 'projects'; mode: 'list' | 'grid' }): void {
    this.analytics.track(AnalyticsEventName.VIEW_MODE_TOGGLED, { surface: input.surface, mode: input.mode });
  }

  tabNavigated(tab: string, context?: { projectId?: string }): void {
    this.analytics.track(AnalyticsEventName.TAB_NAVIGATED, {
      tab,
      ...(context?.projectId ? { project_id: context.projectId } : {}),
    });
  }

  galleryOpened(input: { projectId: string; role: 'coordinator' | 'owner_preview' }): void {
    this.analytics.track(AnalyticsEventName.GALLERY_OPENED, { project_id: input.projectId, role: input.role });
  }
}
